import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

/**
 * Multi-select dropdown component for Super Admin Portal
 * Supports selecting multiple options with search functionality
 */
const MultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select items...",
  className = "",
  disabled = false,
  loading = false,
  searchPlaceholder = "Search...",
  emptyMessage = "No items found",
  maxDisplayItems = 3
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleOption = (optionValue) => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter(item => item !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  const handleRemoveItem = (itemValue, event) => {
    event.stopPropagation();
    onChange(selected.filter(item => item !== itemValue));
  };

  const getSelectedLabels = () => {
    return selected.map(value => {
      const option = options.find(opt => opt.value === value);
      return option ? option.label : value;
    });
  };

  const selectedLabels = getSelectedLabels();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full min-h-[40px] px-3 py-2 text-left bg-white border border-slate-300 rounded-md
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
          ${disabled ? 'bg-slate-50' : 'hover:border-slate-400'}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {selected.length === 0 ? (
              <span className="text-slate-500">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedLabels.slice(0, maxDisplayItems).map((label, index) => {
                  const value = selected[index];
                  return (
                    <span
                      key={value}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                    >
                      <span className="truncate max-w-[120px]">{label}</span>
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-blue-600"
                        onClick={(e) => handleRemoveItem(value, e)}
                      />
                    </span>
                  );
                })}
                {selected.length > maxDisplayItems && (
                  <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md">
                    +{selected.length - maxDisplayItems} more
                  </span>
                )}
              </div>
            )}
          </div>
          <ChevronDown
            className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-200">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-slate-500">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2">Loading...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-slate-500 text-sm">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={() => handleToggleOption(option.value)}
                    className={`
                      flex items-center px-3 py-2 cursor-pointer text-sm
                      hover:bg-slate-50 transition-colors duration-150
                      ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}
                    `}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="truncate">{option.label}</span>
                      {isSelected && (
                        <Check className="h-4 w-4 text-blue-600 ml-2 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with selection count */}
          {selected.length > 0 && (
            <div className="p-2 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>{selected.length} item{selected.length !== 1 ? 's' : ''} selected</span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
